import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Upload,
  MessageSquarePlus,
  History,
  Folder,
  Rocket,
  Star,
  StarOff,
  Trash2,
} from 'lucide-react';
import { GitHistoryDialog } from '@/components/ai/GitHistoryDialog';
import { DeployDialog } from '@/components/DeployDialog';
import { ProjectDetailsDialog } from '@/components/ProjectDetailsDialog';
import { DeleteProjectDialog } from '@/components/DeleteProjectDialog';
import { SaveCodeDialog } from '@/components/SaveCodeDialog';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useSessionManager } from '@/hooks/useSessionManager';
import { useToast } from '@/hooks/useToast';
import type { Project } from '@/lib/ProjectsManager';

interface ProjectContextMenuProps {
  project: Project;
  children: ReactNode;
  onProjectDeleted?: () => void;
}

export function ProjectContextMenu({
  project,
  children,
  onProjectDeleted,
}: ProjectContextMenuProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectsManager = useProjectsManager();
  const sessionManager = useSessionManager();
  const { toast } = useToast();

  const [favorites, setFavorites] = useLocalStorage<string[]>('project-favorites', []);
  const isFavorite = favorites.includes(project.id);

  // Dialog states
  const [gitHistoryOpen, setGitHistoryOpen] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saveCodeDialogOpen, setSaveCodeDialogOpen] = useState(false);

  const handleNewChat = () => {
    sessionManager.clearSession(project.id);
    navigate(`/project/${project.id}`);
  };

  const handleToggleFavorite = () => {
    if (isFavorite) {
      setFavorites(favorites.filter((id) => id !== project.id));
    } else {
      setFavorites([...favorites, project.id]);
    }
  };

  const handleDeleteProject = async () => {
    try {
      await projectsManager.deleteProject(project.id);

      // Delete the session for this project
      await sessionManager.deleteSession(project.id);

      // Invalidate the projects query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['projects'] });

      toast({
        title: 'Project deleted',
        description: `"${project.name}" has been permanently deleted.`,
      });

      // Call the callback if provided
      onProjectDeleted?.();

      // Navigate to home if we were on this project
      navigate('/');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete project. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <ContextMenuItem onClick={handleNewChat} className="gap-2">
            <MessageSquarePlus className="h-4 w-4" />
            New Chat
          </ContextMenuItem>

          <ContextMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setTimeout(() => setSaveCodeDialogOpen(true), 0);
            }}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Save Code
          </ContextMenuItem>

          <ContextMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setTimeout(() => setGitHistoryOpen(true), 0);
            }}
            className="gap-2"
          >
            <History className="h-4 w-4" />
            Rollback
          </ContextMenuItem>

          <ContextMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setTimeout(() => setDeployDialogOpen(true), 0);
            }}
            className="gap-2"
          >
            <Rocket className="h-4 w-4" />
            Deploy
          </ContextMenuItem>

          <ContextMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setTimeout(() => setProjectDetailsOpen(true), 0);
            }}
            className="gap-2"
          >
            <Folder className="h-4 w-4" />
            Project Details
          </ContextMenuItem>

          <ContextMenuSeparator />

          <ContextMenuItem onClick={handleToggleFavorite} className="gap-2">
            {isFavorite ? (
              <>
                <StarOff className="h-4 w-4" />
                Remove from Favorites
              </>
            ) : (
              <>
                <Star className="h-4 w-4" />
                Add to Favorites
              </>
            )}
          </ContextMenuItem>

          <ContextMenuSeparator />

          <ContextMenuItem
            onSelect={(e) => {
              e.preventDefault();
              // Delay opening the dialog to ensure context menu closes first
              setTimeout(() => setDeleteDialogOpen(true), 0);
            }}
            className="gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            Delete Project
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Dialogs */}
      <GitHistoryDialog
        projectId={project.id}
        open={gitHistoryOpen}
        onOpenChange={setGitHistoryOpen}
      />

      <DeployDialog
        projectId={project.id}
        projectName={project.name}
        open={deployDialogOpen}
        onOpenChange={setDeployDialogOpen}
      />

      <ProjectDetailsDialog
        project={project}
        open={projectDetailsOpen}
        onOpenChange={setProjectDetailsOpen}
      />

      <DeleteProjectDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        projectName={project.name}
        onConfirmDelete={handleDeleteProject}
      />

      <SaveCodeDialog
        projectId={project.id}
        open={saveCodeDialogOpen}
        onOpenChange={setSaveCodeDialogOpen}
      />
    </>
  );
}
